// Whole-interface translation: when `uiLanguage` is "zh", walks the DOM and
// replaces English text nodes with Chinese via the same self-hosted
// pprgb-translate service the per-message chat button uses. Chat message
// content is excluded (marked `data-no-translate` in ChatMessage.svelte) —
// it already has its own per-message translate control, and constantly
// re-translating live chat text would fight with that.
//
// This is deliberately NOT a real i18n system (Flotilla has none, and
// hand-translating ~400 components isn't practical to promise) — it's a
// pragmatic stand-in: real UI strings translated live, cached so repeat
// visits are instant. Quality on short 1-3 word labels (most of the UI) is
// noticeably weaker than on full sentences — a known limitation of the
// small translation model, not something this layer can fix.
import {synced} from "@welshman/store"
import {kv} from "@app/storage"
import {translateText} from "@app/translate"

export const uiLanguage = synced({
  key: "uiLanguage",
  defaultValue: "en" as "en" | "zh",
  storage: kv,
})

const CACHE_KEY = "pprgb:ui-translate-cache"
const cache: Record<string, string> = (() => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}")
  } catch {
    return {}
  }
})()

const saveCache = () => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
  } catch {
    // storage full or unavailable — cache just won't persist across reloads
  }
}

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "TEXTAREA", "INPUT", "CODE", "PRE"])

const isTranslatable = (node: Text): boolean => {
  const text = node.textContent?.trim()
  if (!text || text.length < 2 || !/[a-zA-Z]/.test(text)) return false

  const parent = node.parentElement
  if (!parent || SKIP_TAGS.has(parent.tagName) || parent.isContentEditable) return false
  if (parent.closest("[data-no-translate]")) return false

  return true
}

const translateNode = async (node: Text) => {
  const original = node.textContent!
  const key = original.trim()

  const apply = (result: string) => {
    // The node may have moved on or been recycled by Svelte by the time a
    // fetch resolves — only touch it if it still holds the text we translated.
    if (node.isConnected && node.textContent?.trim() === key) {
      node.textContent = original.replace(key, result)
    }
  }

  if (cache[key]) {
    apply(cache[key])
    return
  }

  try {
    const translated = await translateText(key, {source: "en", target: "zh"})
    cache[key] = translated
    saveCache()
    apply(translated)
  } catch {
    // best-effort — leave the English text in place on failure
  }
}

const walk = (root: Node) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let node: Node | null

  while ((node = walker.nextNode())) {
    if (isTranslatable(node as Text)) {
      translateNode(node as Text)
    }
  }
}

let observer: MutationObserver | undefined

export const startUiTranslation = () => {
  if (observer) return

  walk(document.body)

  observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const added of mutation.addedNodes) {
        if (added.nodeType === Node.TEXT_NODE) {
          if (isTranslatable(added as Text)) translateNode(added as Text)
        } else if (added.nodeType === Node.ELEMENT_NODE) {
          walk(added)
        }
      }
    }
  })

  observer.observe(document.body, {childList: true, subtree: true})
}

export const stopUiTranslation = () => {
  observer?.disconnect()
  observer = undefined
}
